import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { MessageCircle, User } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { validateFormInput } from '@/lib/utils';
import { secureLog, RateLimiter } from '@/lib/security';
import { trackEvent } from '@/components/GoogleAnalytics';

interface ContactModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ContactModal = ({ isOpen, onClose }: ContactModalProps) => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Rate limiting: 5 attempts per hour per IP (approximated by browser session)
  const [rateLimiter] = useState(() => new RateLimiter(5, 60 * 60 * 1000));

  useEffect(() => {
    if (isOpen) {
      trackEvent('contact_form_opened');
    }
  }, [isOpen]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Check rate limiting
    const clientId = 'contact-form'; // Simple identifier for this session
    if (rateLimiter.isLimited(clientId)) {
      const remainingTime = Math.ceil(rateLimiter.getRemainingTime(clientId) / (60 * 1000)); // Convert to minutes
      toast({
        title: "Too many submissions",
        description: `Please wait ${remainingTime} minutes before submitting another message.`,
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    // Validate and sanitize all form fields
    const nameValidation = validateFormInput(formData.name, { required: true, maxLength: 100 });
    const emailValidation = validateFormInput(formData.email, { 
      required: true, 
      pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
      maxLength: 255
    });
    const subjectValidation = validateFormInput(formData.subject, { required: true, maxLength: 200 });
    const messageValidation = validateFormInput(formData.message, { required: true, maxLength: 1000 });

    if (!nameValidation.isValid) {
      toast({
        title: "Invalid name",
        description: nameValidation.error,
        variant: "destructive"
      });
      setIsSubmitting(false);
      return;
    }

    if (!emailValidation.isValid) {
      toast({
        title: "Invalid email address",
        description: emailValidation.error,
        variant: "destructive",
      });
      setIsSubmitting(false);
      return;
    }

    if (!subjectValidation.isValid) {
      toast({
        title: "Invalid subject",
        description: subjectValidation.error,
        variant: "destructive"
      });
      setIsSubmitting(false);
      return;
    }

    if (!messageValidation.isValid) {
      toast({
        title: "Invalid message",
        description: messageValidation.error,
        variant: "destructive"
      });
      setIsSubmitting(false);
      return;
    }

    try {
      secureLog.info('Sending contact form submission');
      
      const { data, error } = await supabase.functions.invoke('send-contact-email', {
        body: {
          name: nameValidation.sanitized,
          email: emailValidation.sanitized,
          subject: subjectValidation.sanitized,
          message: messageValidation.sanitized,
        }
      });

      if (error) {
        rateLimiter.recordAttempt(clientId); // Record failed attempt
        throw error;
      }

      secureLog.info('Contact form submitted successfully');
      
      trackEvent('contact_form_submitted', { success: true });
      
      toast({
        title: "Message sent successfully!",
        description: data?.message || "Thank you for your message. We'll get back to you within 24-48 hours.",
      });
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
      onClose();
    } catch (error: any) {
      secureLog.error('Contact form submission failed', error);
      toast({
        title: "Failed to send message",
        description: error.message || "Please try again or contact us directly at info@sinkbikerun.com",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className="max-w-2xl max-h-[90vh] md:max-h-[90vh] overflow-y-auto w-[calc(100vw-2rem)] sm:w-full"
        aria-describedby="contact-modal-description"
      >
        <DialogHeader>
          <DialogTitle className="text-2xl font-display font-bold">Contact</DialogTitle>
          <DialogDescription id="contact-modal-description" className="font-body leading-[1.7]">
            Need advice on race preparation? Have recommendations on how to improve the site? I'd love to hear from you!
          </DialogDescription>
        </DialogHeader>
        
        <div className="mt-4">
          {/* Contact Form */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-display font-semibold">
                <MessageCircle className="w-5 h-5" />
                Send Me a Message
              </CardTitle>
              <CardDescription className="font-body leading-[1.7]">
                Fill out the form below and I'll get back to you as soon as possible.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Name */}
                <div className="space-y-2">
                  <Label htmlFor="name">Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="Your full name"
                    maxLength={100}
                    required
                    aria-required="true"
                    aria-describedby="name-help"
                    className="text-base"
                  />
                  <div id="name-help" className="sr-only">Required field for your full name</div>
                </div>

                {/* Email */}
                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="your.email@example.com"
                    required
                    aria-required="true"
                    aria-describedby="email-help"
                    className="text-base"
                  />
                  <div id="email-help" className="sr-only">Required field for your email address</div>
                </div>

                {/* Subject */}
                <div className="space-y-2">
                  <Label htmlFor="subject">Subject *</Label>
                  <Input
                    id="subject"
                    value={formData.subject}
                    onChange={(e) => handleInputChange('subject', e.target.value)}
                    placeholder="Brief description of your inquiry"
                    maxLength={200}
                    required
                    aria-required="true"
                    aria-describedby="subject-help"
                    className="text-base"
                  />
                  <div id="subject-help" className="sr-only">Required field for the subject of your message</div>
                </div>

                {/* Message */}
                <div className="space-y-2">
                  <Label htmlFor="message">Message *</Label>
                  <Textarea
                    id="message"
                    value={formData.message}
                    onChange={(e) => handleInputChange('message', e.target.value)}
                    placeholder="Tell me about your question or how I can help..."
                    rows={4}
                    maxLength={1000}
                    required
                    aria-required="true"
                    aria-describedby="message-help"
                    className="text-base"
                  />
                  <div id="message-help" className="sr-only">Required field for your detailed message (max 1000 characters)</div>
                </div>

                {/* Submit Button */}
                <Button 
                  type="submit" 
                  variant="accent"
                  className="w-full" 
                  disabled={isSubmitting}
                  aria-describedby="submit-help"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </Button>
                <div id="submit-help" className="sr-only">Sends your message directly to the site owner</div>
              </form>
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ContactModal;